import type { Cycle, Teacher } from "@/types";

/** Nombre de semaines retenu pour le calcul du salaire mensuel. */
export const WEEKS_PER_MONTH = 4;

/** Salaire mensuel : le salaire fixe s'il y en a un, sinon heures / semaine × tarif horaire × semaines par mois. */
export function monthlySalary(teacher: Teacher): number {
  if (teacher.fixedMonthlySalary) return teacher.fixedMonthlySalary;
  return Math.round((teacher.weeklyHours ?? 0) * (teacher.hourlyRate ?? 0) * WEEKS_PER_MONTH);
}

/** Vrai si le salaire de l'enseignant est connu (salaire fixe, ou heures et tarif exploitables). */
export function isRated(teacher: Teacher): boolean {
  return (teacher.fixedMonthlySalary ?? 0) > 0 || ((teacher.weeklyHours ?? 0) > 0 && (teacher.hourlyRate ?? 0) > 0);
}

/** Synthèse de la masse salariale d'un cycle (avec le détail des enseignants). */
export interface PayrollCycle {
  id: string;
  label: string;
  teachers: Teacher[];
  count: number;
  hours: number;
  payroll: number;
}

export interface PayrollSummary {
  cycles: PayrollCycle[];
  totalPayroll: number;
  totalHours: number;
  totalCount: number;
}

/**
 * Construit la masse salariale par cycle à partir des heures / semaine et des
 * tarifs horaires saisis dans la section Enseignants.
 */
export function buildPayroll(teachers: Teacher[], allCycles: Cycle[]): PayrollSummary {
  const cycles: PayrollCycle[] = allCycles.map((cycle) => {
    const list = teachers.filter((t) => t.cycleId === cycle.id);
    return {
      id: cycle.id,
      label: cycle.label,
      teachers: list,
      count: list.length,
      hours: list.reduce((sum, t) => sum + (t.weeklyHours ?? 0), 0),
      payroll: list.reduce((sum, t) => sum + monthlySalary(t), 0),
    };
  });

  return {
    cycles,
    totalPayroll: cycles.reduce((sum, c) => sum + c.payroll, 0),
    totalHours: cycles.reduce((sum, c) => sum + c.hours, 0),
    totalCount: cycles.reduce((sum, c) => sum + c.count, 0),
  };
}

export interface TeacherPayrollForCycle {
  count: number;
  hours: number;
  payroll: number;
}

/** Masse salariale des enseignants pour un cycle donné. */
export function teacherPayrollForCycle(
  teachers: Teacher[],
  cycleId: string,
): TeacherPayrollForCycle {
  const list = teachers.filter((t) => t.cycleId === cycleId);
  return {
    count: list.length,
    hours: list.reduce((sum, t) => sum + (t.weeklyHours ?? 0), 0),
    payroll: list.reduce((sum, t) => sum + monthlySalary(t), 0),
  };
}